import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import TextField, { TextFieldProps } from "@mui/material/TextField";

type TextInputProps = Omit<TextFieldProps, "onChange" | "value"> & {
  initialValue: string;
  onChange: (value: string) => void;
  debounceMs?: number;
};

export const TextInput = ({
  initialValue,
  onChange,
  debounceMs: debounceMsProp,
  error,
  helperText,
  onBlur,
  onFocus,
  ...props
}: TextInputProps) => {
  const debounceMs = debounceMsProp ?? 500;

  const [localValue, setLocalValue] = useState(initialValue);
  const localValueRef = useRef(initialValue);
  const onChangeRef = useRef(onChange);
  const isFocusedRef = useRef(false);
  const pendingLocalValueRef = useRef<string | null>(null);
  const previousInitialValueRef = useRef(initialValue);
  const initialValueChangedWhileFocusedRef = useRef(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const hasPendingLocalValue = localValue !== initialValue;
  const hasRequiredWhitespaceOnlyValue = Boolean(props.required && localValue.length > 0 && localValue.trim() === "");
  const requiredHelperText =
    helperText ??
    (typeof props.label === "string" && props.label.length > 0
      ? `${props.label} is a required field.`
      : "This is a required field.");

  useLayoutEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  const clearPendingTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const commitPendingLocalValue = useCallback(() => {
    const pendingValue = pendingLocalValueRef.current;
    if (pendingValue === null) return false;

    clearPendingTimer();
    pendingLocalValueRef.current = null;
    onChangeRef.current(pendingValue);
    return true;
  }, [clearPendingTimer]);

  useEffect(() => {
    if (previousInitialValueRef.current === initialValue) return;

    previousInitialValueRef.current = initialValue;
    if (initialValue === localValueRef.current) {
      clearPendingTimer();
      pendingLocalValueRef.current = null;
      initialValueChangedWhileFocusedRef.current = false;
      return;
    }

    if (pendingLocalValueRef.current !== null) return;
    if (isFocusedRef.current) {
      initialValueChangedWhileFocusedRef.current = true;
      return;
    }

    localValueRef.current = initialValue;
    initialValueChangedWhileFocusedRef.current = false;
    setLocalValue(initialValue);
  }, [clearPendingTimer, initialValue]);

  useEffect(() => {
    return () => {
      // Route/tab transitions can unmount the field without producing blur
      // (the issue traverser deliberately preserves focus on mousedown). Flush
      // the local edit so navigation cannot discard the last debounce window.
      commitPendingLocalValue();
    };
  }, [commitPendingLocalValue]);

  function handleChange(event: React.ChangeEvent<HTMLInputElement>) {
    const newValue = event.target.value;

    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    localValueRef.current = newValue;
    pendingLocalValueRef.current = newValue;
    initialValueChangedWhileFocusedRef.current = false;
    setLocalValue(newValue);

    if (debounceMs <= 0) {
      timerRef.current = null;
      pendingLocalValueRef.current = null;
      onChangeRef.current(newValue);
      return;
    }

    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      if (pendingLocalValueRef.current === null) return;
      pendingLocalValueRef.current = null;
      onChangeRef.current(newValue);
    }, debounceMs);
  }

  function handleBlur(event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) {
    isFocusedRef.current = false;
    const committedPendingValue = commitPendingLocalValue();
    if (
      !committedPendingValue &&
      initialValueChangedWhileFocusedRef.current &&
      localValueRef.current !== previousInitialValueRef.current
    ) {
      localValueRef.current = previousInitialValueRef.current;
      setLocalValue(previousInitialValueRef.current);
    }
    initialValueChangedWhileFocusedRef.current = false;
    onBlur?.(event);
  }

  function handleFocus(event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) {
    isFocusedRef.current = true;
    onFocus?.(event);
  }

  const displayedError = hasRequiredWhitespaceOnlyValue || (hasPendingLocalValue ? false : error);
  let displayedHelperText = helperText;

  if (hasRequiredWhitespaceOnlyValue) {
    displayedHelperText = requiredHelperText;
  } else if (hasPendingLocalValue) {
    displayedHelperText = undefined;
  }

  return (
    <TextField
      {...props}
      error={displayedError}
      helperText={displayedHelperText}
      value={localValue}
      onChange={handleChange}
      onBlur={handleBlur}
      onFocus={handleFocus}
    />
  );
};
